import React, { memo, useState } from 'react'
import { useDispatch } from 'react-redux'
import { STABLE_EMPTY_OBJECT } from '@feedyou/utils'
import { BotFeatures } from '@feedyou-ai/feedbot-schema'

import { useBotFlow, useIsTelephony } from '../../../store/flow'
import { getUiSchema } from './uiSchemaService'
import { FooterContent } from './FooterContent'
import { SettingsPopup, SettingsPopupProps } from '../SettingsPopup'
import { getFormData, getSchema, nullifyDeletedFlowProperties, save } from './utils'
import { useFeature } from '../../../store/features'
import { useOpenModalOpenSection } from '../../../store/modals/hooks/useOpenModalOpenSection'
import { modalsActions } from '../../../store/modals/modalsSlice'
import { useSettingsPopupState } from '../hooks'
import { BotConfigurationForm } from './BotConfigurationForm/BotConfigurationForm'
import { BotPermissionsForm } from './BotPermissionsForm/BotPermissionsForm'
import { validateAndCorrectBotJson } from '../../../features/flowValidation'
import { WizardConfigurationForm } from './WizardConfigurationForm/WizardConfigurationForm'
import { useSdkFeature } from '../../../features/hostingVersions'

export const BotSettingsPopup: React.FC = memo(() => {
  const flow = useBotFlow()
  const dispatch = useDispatch()
  const isTelephony = useIsTelephony()
  const openSection = useOpenModalOpenSection() ?? 'form'
  const isGdprSettingsEnabled = !!useFeature('GDPR_SETTINGS')

  const isWizardViewEnabled = useSdkFeature(BotFeatures.WIZARD_MODE)
  const isIdValidationEnabled = !!useFeature('UNIQUE_BOT_ID_VALIDATION')
  const [showDialogsCheckboxState, setShowDialogsCheckboxState] = useState(false)

  const stripDialogsFromState = openSection === 'json' && !showDialogsCheckboxState
  const { isTouched, initialFormData, formData, onFormDataChange } = useSettingsPopupState(
    () => getFormData(flow!, !stripDialogsFromState),
    [openSection, stripDialogsFromState]
  )

  if (!flow) {
    return null
  }

  const schema = openSection === 'form' ? getSchema() : STABLE_EMPTY_OBJECT

  return (
    <SettingsPopup
      schema={schema}
      openSection={openSection}
      initialFormData={initialFormData}
      // Prevent expensive double-validation: Fields already get liveValidated inside CollapsibleObjects
      liveValidate={false}
      className="no-error-panel"
      customTabNames={{ form: 'Flow form', json: 'Flow JSON' }}
      validationErrorsByField={{}}
      uiSchema={getUiSchema({ isTelephony, isGdprSettingsEnabled })}
      formData={formData}
      footerContent={
        openSection === 'json' && (
          <FooterContent
            showDialogs={showDialogsCheckboxState}
            isDisabled={isTouched}
            onChange={() => setShowDialogsCheckboxState(!showDialogsCheckboxState)}
          />
        )
      }
      close={() => dispatch(modalsActions.closeModal())}
      save={async formData => {
        // This is to keep any dialogs that have been created by DialogSelectWidget in the form since its formData was populated. This is a hacky way to do it, but it works.
        // This works because we currently never edit/delete dialogs from the form. If we ever do, we'll need to revisit this.
        const data = openSection === 'json' ? formData : { ...formData, dialogs: flow.dialogs }
        const result = await validateAndCorrectBotJson({ flow: data, isIdValidationEnabled })
        if (!result?.didValidateSuccessfully) return

        const flowToSave = nullifyDeletedFlowProperties({
          previousFlow: flow,
          newFlow: result.correctedEntity
        })

        dispatch(save(flowToSave))
      }}
      onChange={onFormDataChange}
      additionalSections={[
        {
          tab: { section: 'general', label: 'General' },
          renderContent: props => <BotConfigurationForm {...props} />
        },
        {
          tab: { section: 'permissions', label: 'Permissions' },
          renderContent: props => <BotPermissionsForm {...props} />
        },
        ...(isWizardViewEnabled
          ? [
              {
                tab: { section: 'wizard' as const, label: 'Wizard' },
                renderContent: (props: SettingsPopupProps) => <WizardConfigurationForm {...props} />
              }
            ]
          : [])
      ]}
      tabOrder={{
        general: 1,
        permissions: 2,
        ...(isWizardViewEnabled ? { wizard: 3 } : {}),
        form: isWizardViewEnabled ? 4 : 3,
        json: isWizardViewEnabled ? 5 : 4
      }}
    />
  )
})
